import { Coordinates, MapIsochroneSetting } from '@bpd-library/types';
import { calculateBoundingBox, environmentDetails } from '@bpd-library/utilities';
import mapboxgl from 'mapbox-gl';

const OUTER_COORDINATES: Coordinates = [
    [
        [-42, 74],
        [-42, 1],
        [80, 1],
        [80, 74],
        [-42, 74],
    ],
];
const ISOCHRONE_SOURCE = 'isochrone';
const DEFAULT_FILL_COLOR = '#000000';
const DEFAULT_STROKE_COLOR = '#32C1DE';

type IsochroneApiResult = GeoJSON.FeatureCollection<GeoJSON.Polygon>;

let mask: any;
let polygon: any;

const addIsochrone = async (map: mapboxgl.Map, isochroneSetting: MapIsochroneSetting) => {
    await init();

    async function init() {
        // Lazyload turf package
        if (!mask || !polygon) {
            const maskPackage = await import('@turf/mask');
            const helpersPackage = await import('@turf/helpers');

            mask = await maskPackage.default;
            polygon = await helpersPackage.polygon;
        }

        // Setup source and layer, if not existing
        if (!map.getSource(ISOCHRONE_SOURCE)) {
            setupSource();
            setupLayer();
        }

        const { coordinate, minutes, profile, fitBounds } = isochroneSetting;

        const accessToken = environmentDetails.mapboxAccessToken || '';

        try {
            let url: string = `https://api.mapbox.com/isochrone/v1/mapbox/${profile}/${coordinate[0]},${coordinate[1]}?contours_minutes=${minutes}&polygons=true&access_token=${accessToken}`;

            // To simplify the polygon. Makes calculating boundingBoxes more performant
            if (profile === 'driving') {
                url += '&generalize=100';
            }

            const result = await fetch(url);
            const parsedResult: IsochroneApiResult = await result.json();

            if (parsedResult) {
                const source = map.getSource(ISOCHRONE_SOURCE) as
                    | mapboxgl.GeoJSONSource
                    | undefined;

                const invertedCoordinates = invertCoordinates(parsedResult);

                if (fitBounds) {
                    fitApiResultBounds(parsedResult);
                }

                source?.setData(invertedCoordinates);
            }
        } catch (e) {
            if (environmentDetails.isLocal) {
                console.error(e);
            }
        }
    }

    function setupSource() {
        map.addSource(ISOCHRONE_SOURCE, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [],
            },
        });
    }

    function setupLayer() {
        const { fill = DEFAULT_FILL_COLOR, stroke = DEFAULT_STROKE_COLOR } =
            isochroneSetting.color || {};

        map.addLayer({
            id: 'isochroneFill',
            type: 'fill',
            source: ISOCHRONE_SOURCE,
            layout: {},
            paint: {
                'fill-color': fill,
                'fill-opacity': 0.07,
            },
        });

        map.addLayer({
            id: 'isochroneStroke',
            type: 'line',
            source: ISOCHRONE_SOURCE,
            layout: {},
            paint: {
                'line-color': stroke,
                'line-width': 1,
            },
        });
    }

    function invertCoordinates(apiResult: IsochroneApiResult) {
        const apiCoordinates = apiResult.features[0]?.geometry.coordinates;

        const outerPolygon = polygon(apiCoordinates);
        const innerPolygon = polygon([...OUTER_COORDINATES]);

        const invertedPolygon = mask(outerPolygon, innerPolygon);

        return invertedPolygon;
    }

    function fitApiResultBounds(apiResult: IsochroneApiResult) {
        const coordinates = apiResult.features[0]?.geometry.coordinates as Coordinates;
        const boundingBox = calculateBoundingBox(coordinates);

        map.fitBounds(boundingBox, {
            padding: 20,
            animate: true,
            duration: 1000,
        });
    }
};

const removeIsochrone = (map: mapboxgl.Map) => {
    const source = map.getSource(ISOCHRONE_SOURCE) as mapboxgl.GeoJSONSource | undefined;

    if (!source) return;

    source.setData({
        type: 'FeatureCollection',
        features: [],
    });
};

export { addIsochrone, removeIsochrone };
